import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { PageLoading, SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { App, ConfigProvider } from 'antd';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';

import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { getUserInfo } from './services/user';
import './global.less';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

// 酒馆棕色主题 token 配置
const themeToken = {
  token: {
    colorPrimary: '#5c3920',
    borderRadius: 8,
  },
  components: {
    Button: {
      colorPrimary: '#5c3920',
      colorPrimaryHover: '#7a4a25',
      colorPrimaryActive: '#4f3019',
      colorPrimaryText: '#ffffff',
      colorPrimaryBg: '#5c3920',
      colorPrimaryBgHover: '#7a4a25',
      colorPrimaryBorder: '#5c3920',
      colorPrimaryBorderHover: '#7a4a25',
      // controlHeight: 40,
      fontWeight: 500,
    },
    Select: {
      // 下拉选项选中/hover 态：浅棕色系（全局统一，替代默认深色）
      optionSelectedBg: '#f3e3cb',
      optionSelectedColor: '#8a4b0f',
      optionActiveBg: '#faf3e8',
    },
  },
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.UserFileds;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.UserFileds | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await getUserInfo();
      sessionStorage.setItem('userInfo', JSON.stringify(msg.data));
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (![loginPath].includes(location.pathname)) {
    const currentUser = await fetchUserInfo();

    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      // 登录页不做用户信息拉取；登录成功进入系统后（SPA 跳转不重跑
      // getInitialState），若 currentUser 缺失则在此补拉一次
      const { location } = history;
      if (location.pathname !== loginPath && !initialState?.currentUser) {
        initialState?.fetchUserInfo?.().then((currentUser) => {
          if (currentUser) {
            setInitialState((preInitialState) => ({
              ...preInitialState,
              currentUser,
            }));
          }
        });
      }
    },
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '',
  ...errorConfig,
};

/**
 * @name 根容器配置
 * @description 包装整个应用，提供全局上下文
 */
export function rootContainer(container: React.ReactNode) {
  return (
    <ConfigProvider theme={themeToken}>
      <App>{container}</App>
    </ConfigProvider>
  );
}
